import { Drawer, Input, Select, message, Upload, DatePicker } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { createPfLoan, fetchAllPfLoans, updatePfLoan } from "../../../core/data/redux/payroll/Accounting/providentFund/pfLoanRequest/pfLoanActions";
import { status } from "../../../core/common/selectoption/selectoption";
import { fetchAllPfEmployees } from "../../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";
import dayjs, { Dayjs } from 'dayjs';
import { useAppConfig } from "../../../customhooks/useAppConfig";

const loanReasonOptions = [
    { value: "medical_emergency", label: "Medical Emergency" },
    { value: "education", label: "Education" },
    { value: "marriage", label: "Marriage" },
    { value: "home_purchase", label: "Home Purchase" },
    { value: "home_renovation", label: "Home Renovation" },
    { value: "debt_consolidation", label: "Debt Consolidation" },
    { value: "business_investment", label: "Business Investment" },
    { value: "personal_use", label: "Personal Use" },
    { value: "other", label: "Other" }
];

const requestStatusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Paid", label: "Paid" },
];

const PfLoanRequestModal: React.FC<any> = ({
    modalType,
    handleCancel,
    isModalVisible,
    selectedRowData,
    setTableKey
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const { loadingStates } = useSelector((state: any) => state.providentFund.pfLoanRequest);
    const { records: pfEmployees } = useSelector((state: any) => state.providentFund.pfEmployeeMapping);
    const config = useAppConfig();

    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;

    const [formData, setFormData] = useState<{
        pfEmployeeId: number | undefined;
        requestDate: Dayjs | null;
        amountRequested: string;
        loanReason: string | undefined;
        requeststatus: string;
        repaymentTenureMonths: string;
        monthlyInstallment: string;
        status: string;
        createdBy: number;
    }>({
        pfEmployeeId: undefined,
        requestDate: null,
        amountRequested: "",
        loanReason: undefined,
        requeststatus: "Pending",
        repaymentTenureMonths: "",
        monthlyInstallment: "",
        status: "active",
        createdBy: user?.UserId || 1,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [fileList, setFileList] = useState<any[]>([]);

    useEffect(() => {
        if (!pfEmployees.length) {
            dispatch(fetchAllPfEmployees());
        }

        // Get employee by user ID if not already available
        if (!employeesbyUser?.employeeId && user?.UserId) {
            dispatch(getEmployeeByUserId({ userId: user.UserId }));
        }
    }, [dispatch, pfEmployees.length, employeesbyUser?.employeeId, user?.UserId]);

    const resetForm = useCallback(() => {
        setFormData({
            pfEmployeeId: undefined,
            requestDate: null,
            amountRequested: "",
            loanReason: undefined,
            requeststatus: "Pending",
            repaymentTenureMonths: "",
            monthlyInstallment: "",
            status: "active",
            createdBy: user?.UserId || 1,
        });
        setErrors({});
        setFileList([]);
    }, [user?.UserId]);

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                pfEmployeeId: selectedRowData.pfEmployeeId || undefined,
                requestDate: selectedRowData.requestDate ? dayjs(selectedRowData.requestDate) : null,
                amountRequested: selectedRowData.amountRequested || "",
                loanReason: selectedRowData.loanReason || undefined,
                requeststatus: selectedRowData.requeststatus || "Pending",
                repaymentTenureMonths: selectedRowData.repaymentTenureMonths || "",
                monthlyInstallment: selectedRowData.monthlyInstallment || "",
                status: selectedRowData.status || "active",
                createdBy: selectedRowData.createdBy || user?.UserId || 1,
            });

            // Set file list for edit mode if attachment exists
            if (selectedRowData.attachmentFilePath) {
                setFileList([{
                    uid: '-1',
                    name: selectedRowData.attachmentFilePath.split('/').pop() || 'attachment',
                    status: 'done',
                    url: selectedRowData.attachmentFilePath,
                }]);
            }
            setErrors({});
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData, resetForm, user?.UserId]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));

        // Auto-calculate monthly installment when amount and tenure change
        if (field === "amountRequested" || field === "repaymentTenureMonths") {
            const amount = field === "amountRequested" ? parseFloat(value) : parseFloat(formData.amountRequested);
            const tenure = field === "repaymentTenureMonths" ? parseInt(value) : parseInt(formData.repaymentTenureMonths);

            if (amount > 0 && tenure > 0) {
                const monthlyInstallment = (amount / tenure).toFixed(2);
                setFormData((prev) => ({ ...prev, monthlyInstallment }));
            }
        }
    };

    const handleFileUpload = {
        beforeUpload: (file: File) => {
            const isValidType = file.type === 'application/pdf' ||
                file.type === 'image/jpeg' ||
                file.type === 'image/png' ||
                file.type === 'image/jpg' ||
                file.type.startsWith('application/vnd.openxmlformats') ||
                file.type.startsWith('application/msword');

            if (!isValidType) {
                message.error('Only PDF, DOC, DOCX, JPG, JPEG, PNG files are allowed!');
                return false;
            }

            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error('File must be smaller than 10MB!');
                return false;
            }

            return false; // Prevent auto upload, handle manually
        },
        onChange: (info: any) => {
            const file = info.fileList[0]?.originFileObj;
            setFileList(info.fileList);

            setErrors((prev) => ({
                ...prev,
                attachment: file ? "" : "Attachment is required.",
            }));
        },
        fileList: fileList,
        maxCount: 1,
    };

    const validateForm = () => {
        let valid = true;
        const newErrors: Record<string, string> = {};

        if (!formData.pfEmployeeId) {
            newErrors.pfEmployeeId = "PF Employee is required.";
            valid = false;
        }

        if (!formData.requestDate) {
            newErrors.requestDate = "Request Date is required.";
            valid = false;
        }

        if (!formData.amountRequested || parseFloat(formData.amountRequested) <= 0) {
            newErrors.amountRequested = "Valid loan amount is required.";
            valid = false;
        }

        if (!formData.loanReason) {
            newErrors.loanReason = "Loan Reason is required.";
            valid = false;
        }

        if (!formData.repaymentTenureMonths || parseInt(formData.repaymentTenureMonths) <= 0) {
            newErrors.repaymentTenureMonths = "Valid repayment tenure is required.";
            valid = false;
        }

        if (!formData.monthlyInstallment || parseFloat(formData.monthlyInstallment) <= 0) {
            newErrors.monthlyInstallment = "Valid monthly installment is required.";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const file = fileList[0]?.originFileObj;

        try {
            const submitData = {
                ...formData,
                requestDate: formData.requestDate!.format('YYYY-MM-DD'),
                amountRequested: parseFloat(formData.amountRequested),
                repaymentTenureMonths: parseInt(formData.repaymentTenureMonths),
                monthlyInstallment: parseFloat(formData.monthlyInstallment),
                attachment: file,
            };

            if (modalType === "edit") {
                await dispatch(updatePfLoan({
                    id: selectedRowData.pfLoanId,
                    data: submitData
                })).unwrap();
                message.success("PF Loan Request updated successfully");
            } else {
                await dispatch(createPfLoan(submitData)).unwrap();
                message.success("PF Loan Request created successfully");
            }

            await dispatch(fetchAllPfLoans());
            setTableKey((prevKey: number) => prevKey + 1);

            handleCancel();
            resetForm();
        } catch (error) {
            message.error("Something went wrong. Please try again.");
        }
    };

    // Filter PF employees based on current user if employeesbyUser is available
    const filteredPfEmployees = employeesbyUser?.employeeId
        ? pfEmployees.filter((pfEmp: any) => pfEmp.EmployeeID === employeesbyUser.employeeId)
        : pfEmployees;

    return (
        <Drawer
            title={modalType === "edit" ? "Edit PF Loan Request" : "Add PF Loan Request"}
            placement="right"
            width={500}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
        >
            <form>
                {/* PF Employee */}
                <div className="mb-3">
                    <label className="form-label">PF Employee</label>
                    <Select
                        className="w-100"
                        value={formData.pfEmployeeId}
                        onChange={(value) => handleChange("pfEmployeeId", value)}
                        placeholder="Select PF Employee"
                    >
                        {filteredPfEmployees.map((pfEmployee: any) => (
                            <Select.Option key={pfEmployee.pfEmployeeId} value={pfEmployee.pfEmployeeId}>
                                {pfEmployee?.employeeName || `Employee ID: ${pfEmployee.EmployeeID}`}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.pfEmployeeId && <div className="text-danger mt-1">{errors.pfEmployeeId}</div>}
                </div>

                {/* Request Date */}
                <div className="mb-3">
                    <label className="form-label">Request Date</label>
                    <div className="input-icon-end position-relative">
                        <DatePicker
                            className="w-100"
                            value={formData.requestDate}
                            onChange={(date) => handleChange("requestDate", date)}
                            format="YYYY-MM-DD"
                            placeholder="Select request date"
                        />
                        <span className="input-icon-addon">
                            <i className="ti ti-calendar text-gray-7" />
                        </span>
                    </div>
                    {errors.requestDate && <div className="text-danger mt-1">{errors.requestDate}</div>}
                </div>

                {/* Amount Requested */}
                <div className="mb-3">
                    <label className="form-label">Loan Amount</label>
                    <Input
                        type="number"
                        value={formData.amountRequested}
                        onChange={(e) => handleChange("amountRequested", e.target.value)}
                        placeholder="Enter loan amount"
                        min="0"
                        step="0.01"
                    />
                    {errors.amountRequested && <div className="text-danger mt-1">{errors.amountRequested}</div>}
                </div>

                {/* Loan Reason */}
                <div className="mb-3">
                    <label className="form-label">Loan Reason</label>
                    <Select
                        className="w-100"
                        value={formData.loanReason}
                        onChange={(value) => handleChange("loanReason", value)}
                        placeholder="Select loan reason"
                    >
                        {loanReasonOptions.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.loanReason && <div className="text-danger mt-1">{errors.loanReason}</div>}
                </div>

                {/* Repayment Tenure */}
                <div className="mb-3">
                    <label className="form-label">Repayment Tenure (Months)</label>
                    <Input
                        type="number"
                        value={formData.repaymentTenureMonths}
                        onChange={(e) => handleChange("repaymentTenureMonths", e.target.value)}
                        placeholder="Enter tenure in months"
                        min="1"
                        max="240"
                    />
                    {errors.repaymentTenureMonths && <div className="text-danger mt-1">{errors.repaymentTenureMonths}</div>}
                </div>

                {/* Monthly Installment */}
                <div className="mb-3">
                    <label className="form-label">Monthly Installment</label>
                    <Input
                        type="number"
                        value={formData.monthlyInstallment}
                        onChange={(e) => handleChange("monthlyInstallment", e.target.value)}
                        placeholder="Calculated automatically"
                        min="0"
                        step="0.01"
                        readOnly
                    />
                    {errors.monthlyInstallment && <div className="text-danger mt-1">{errors.monthlyInstallment}</div>}
                </div>

                {/* Request Status */}
                <div className="mb-3">
                    <label className="form-label">Request Status</label>
                    <Select
                        className="w-100"
                        value={formData.requeststatus}
                        onChange={(value) => handleChange("requeststatus", value)}
                        disabled={modalType === "create"}
                    >
                        {(modalType === "edit" ? requestStatusOptions : requestStatusOptions.filter(opt => opt.value === "Pending")).map(
                            (option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            )
                        )}
                    </Select>
                </div>
                {/* Status */}
                <div className="mb-3">
                    <label className="form-label">Status</label>
                    <Select
                        className="w-100"
                        value={formData.status}
                        onChange={(value) => handleChange("status", value)}
                    >
                        {status.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                {/* Attachment Upload */}
                <div className="mb-3">
                    <Upload {...handleFileUpload}>
                        <button type="button" className="btn btn-primary">
                            <UploadOutlined /> Upload File
                        </button>
                    </Upload>
                    <small className="text-muted d-block mt-1">
                        Supported: PDF, DOC, DOCX, JPG, JPEG, PNG (Max: 10MB)
                    </small>
                    {errors.attachment && <div className="text-danger mt-1">{errors.attachment}</div>}
                </div>

                {/* Footer buttons */}
                <div className="modal-footer mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                            handleCancel();
                            resetForm();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update Loan Request" : "Add Loan Request"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default PfLoanRequestModal;